import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Clock, Users, Info } from 'lucide-react';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import doctorService from '../../../services/doctorService';
import CalendarSlotPicker from './CalendarSlotPicker';

const DoctorSlots = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const doctor = state?.doctor;
  const [consultationType, setConsultationType] = useState(''); // 'normal' or 'priority'
  const [selectedSlot, setSelectedSlot] = useState(null);

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleProceed = () => {
    if (selectedSlot && consultationType) {
      navigate('/doctor-booking', { 
        state: { 
          doctor, 
          slot: selectedSlot,
          consultationType 
        } 
      });
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!doctor) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600">No doctor selected.</p>
          <button onClick={handleBack} className="mt-4 text-blue-600 hover:text-blue-800">
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-8">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors font-semibold"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Doctors
            </button>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mx-auto mb-6">
              <User className="h-10 w-10 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Book an Appointment with Dr. {doctor.name}
            </h1>
            <p className="text-gray-600">
              {doctor.specialization || doctor.specialty}
            </p>
          </div>

          {/* Consultation Type Selection */}
          <div className="max-w-4xl mx-auto mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Choose Consultation Type
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Normal Consultation */}
              <div
                onClick={() => {
                  setConsultationType('normal');
                  setSelectedSlot(null);
                }}
                className={`cursor-pointer rounded-xl p-6 border-2 transition-all duration-200 ${
                  consultationType === 'normal'
                    ? 'border-blue-600 bg-blue-50 shadow-lg'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-lg ${
                      consultationType === 'normal' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <Users className={`w-6 h-6 ${
                        consultationType === 'normal' ? 'text-blue-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Normal</h3>
                      <p className="text-2xl font-bold text-green-600 mt-1">
                        ₹{doctor.price_hospital_normal || 400}
                      </p>
                    </div>
                  </div>
                  {consultationType === 'normal' && (
                    <div className="bg-blue-600 text-white rounded-full p-1">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      Doctor will see you based on your <strong>arrival time at the hospital</strong>
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      <strong>Queue system:</strong> First come, first served basis
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      Select <strong>only the date</strong> - no specific time slot required
                    </p>
                  </div>
                </div>
              </div>

              {/* Priority Consultation */}
              <div
                onClick={() => {
                  setConsultationType('priority');
                  setSelectedSlot(null);
                }}
                className={`cursor-pointer rounded-xl p-6 border-2 transition-all duration-200 ${
                  consultationType === 'priority'
                    ? 'border-purple-600 bg-purple-50 shadow-lg'
                    : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-lg ${
                      consultationType === 'priority' ? 'bg-purple-100' : 'bg-gray-100'
                    }`}>
                      <Clock className={`w-6 h-6 ${
                        consultationType === 'priority' ? 'text-purple-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Priority</h3>
                      <p className="text-2xl font-bold text-green-600 mt-1">
                        ₹{doctor.price_hospital_priority || 500}
                      </p>
                    </div>
                  </div>
                  {consultationType === 'priority' && (
                    <div className="bg-purple-600 text-white rounded-full p-1">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      Choose your <strong>preferred time slot</strong>
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      <strong>Guaranteed appointment</strong> at selected time
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      <strong>No waiting</strong> - doctor will see you on scheduled time
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Slot Picker - Only show after consultation type is selected */}
          {consultationType && (
            <div className="max-w-4xl mx-auto">
              <CalendarSlotPicker
                doctorId={doctor.id}
                onSlotSelect={handleSlotSelect}
                doctorService={doctorService}
                showTimeSlots={consultationType === 'priority'}
                consultationType={consultationType}
              />
            </div>
          )}

          {/* Proceed Button */}
          {selectedSlot && consultationType && (
            <div className="mt-8 flex flex-col items-center space-y-4">
              <div className={`px-6 py-3 rounded-lg ${
                consultationType === 'priority' ? 'bg-purple-100 border border-purple-300' : 'bg-blue-100 border border-blue-300'
              }`}>
                <p className="text-sm font-medium text-gray-700">
                  <span className="font-bold">
                    {consultationType === 'normal' ? 'Normal Consultation' : 'Priority Consultation'}
                  </span>
                  {' - '}
                  Date: <span className="font-semibold">{selectedSlot.date}</span>
                  {consultationType === 'priority' && selectedSlot.time && (
                    <> at <span className="font-semibold">{selectedSlot.time}</span></>
                  )}
                </p>
              </div>
              <button
                onClick={handleProceed}
                className={`${
                  consultationType === 'priority' 
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                } text-white font-bold py-4 px-12 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
              >
                Proceed to Booking Details
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DoctorSlots;